import { isIntlValidLocale, toApiV2Language } from '@lumapps/translations';

/**
 * Resolve a language code to the English name of the language.
 * For example, `fr` gives `French`, and `pt_br` gives `Brazilian Portuguese`.
 *
 * The name is in English, not in the interface language. The code puts this name in an AI prompt.
 * A prompt that names the target language gives a more reliable result.
 *
 * @param language an API v1 (`pt_br`) or API v2 (`pt-BR`) language code
 * @returns the English name of the language, or `undefined` when the code does not resolve
 */
export const getEnglishLanguageName = (language?: string): string | undefined => {
    if (!language) {
        return undefined;
    }

    /** The Intl API does not always support a legacy v1 language code. */
    const locale = toApiV2Language(language);

    if (!isIntlValidLocale(locale)) {
        return undefined;
    }

    /**
     * With this option, the Intl API gives `undefined` for a well-formed but unknown locale.
     * Without this option, the API gives the raw language code.
     */
    return new Intl.DisplayNames(['en'], { type: 'language', fallback: 'none' }).of(locale);
};
